import type { AgentSummary, PublicMessage, WildsEvent } from '@machine-wilds/protocol';

export const INTERACTION_LIFETIME_MS = 4_000;
export const MAX_ACTIVE_INTERACTIONS = 20;
const MAX_SEEN_EVENT_IDS = 500;

export interface PublicInteraction {
  eventId: string;
  messageId: string;
  senderId: string;
  targetIds: string[];
  startedAt: number;
}

export interface InteractionState {
  active: PublicInteraction[];
  seenEventIds: string[];
}

export type InteractionAction =
  | {
      type: 'event';
      event: WildsEvent;
      population: AgentSummary[];
      now: number;
    }
  | { type: 'expire'; eventId: string };

export const initialInteractionState: InteractionState = {
  active: [],
  seenEventIds: [],
};

export function interactionReducer(
  state: InteractionState,
  action: InteractionAction,
): InteractionState {
  if (action.type === 'expire') {
    return {
      ...state,
      active: state.active.filter((interaction) => interaction.eventId !== action.eventId),
    };
  }

  const interaction = publicInteraction(action.event, action.population, action.now);
  if (!interaction || state.seenEventIds.includes(interaction.eventId)) return state;
  return {
    active: [...state.active, interaction].slice(-MAX_ACTIVE_INTERACTIONS),
    seenEventIds: [...state.seenEventIds, interaction.eventId].slice(-MAX_SEEN_EVENT_IDS),
  };
}

export function publicInteraction(
  event: WildsEvent,
  population: AgentSummary[],
  now: number,
): PublicInteraction | undefined {
  if (event.type !== 'message.created' || !isPublicMessage(event.data)) return undefined;
  const message = event.data;
  return {
    eventId: event.eventId,
    messageId: message.id,
    senderId: message.sender.id,
    targetIds: population.map((agent) => agent.id).filter((id) => id !== message.sender.id),
    startedAt: now,
  };
}

export function interactionExpiryDelay(interaction: PublicInteraction, now: number): number {
  return Math.max(0, interaction.startedAt + INTERACTION_LIFETIME_MS - now);
}

export function agentPosition(id: string, axis: 'x' | 'y'): number {
  let hash = axis === 'x' ? 2166136261 : 3339675911;
  for (const character of id) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  return 12 + (Math.abs(hash) % 76);
}

export function connectionPath(sourceId: string, targetId: string, index: number): string {
  const source = { x: agentPosition(sourceId, 'x'), y: agentPosition(sourceId, 'y') };
  const target = { x: agentPosition(targetId, 'x'), y: agentPosition(targetId, 'y') };
  const deltaX = target.x - source.x;
  const deltaY = target.y - source.y;
  const distance = Math.hypot(deltaX, deltaY) || 1;
  const trim = Math.min(4, distance / 4);
  const startX = source.x + (deltaX / distance) * trim;
  const startY = source.y + (deltaY / distance) * trim;
  const endX = target.x - (deltaX / distance) * trim;
  const endY = target.y - (deltaY / distance) * trim;
  const bend = Math.min(3.5, distance * 0.08) * (index % 2 === 0 ? 1 : -1);
  const controlX = (startX + endX) / 2 - (deltaY / distance) * bend;
  const controlY = (startY + endY) / 2 + (deltaX / distance) * bend;
  return `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
}

function isPublicMessage(value: unknown): value is PublicMessage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<PublicMessage>;
  return (
    candidate.kind === 'public' &&
    typeof candidate.id === 'string' &&
    typeof candidate.sender === 'object' &&
    candidate.sender !== null &&
    typeof candidate.sender.id === 'string'
  );
}
